import { Component, Inject, OnInit } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Observable } from 'rxjs';
import { map, startWith, take } from 'rxjs/operators';
import {
  CITIES_LABEL,
  COUNTRIES_LABEL,
  FilterDisplaySettings,
  Location,
  LocationGroup,
  STATEPROV_LABEL,
} from '../brand-filter-container.service';

export interface GeographyData {
  availableLocations: LocationGroup[];
  selectedCountries: Location[];
  selectedStateProvinces: Location[];
  selectedCities: Location[];
  filterSettings: FilterDisplaySettings;
}

@Component({
  selector: 'bc-brand-geography-dialog',
  templateUrl: './brand-geography-dialog.component.html',
  styleUrls: ['./brand-geography-dialog.component.scss'],
  standalone: false,
})
export class BrandGeographyDialogComponent implements OnInit {
  locationControl = new UntypedFormControl();

  filteredLocations$: Observable<any[]>;

  selectedOptions: string[] = [];
  selectedCountries: Location[] = [];
  selectedStateProvinces: Location[] = [];
  selectedCities: Location[] = [];

  constructor(
    public dialogRef: MatDialogRef<BrandGeographyDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: GeographyData,
  ) {}

  ngOnInit(): void {
    this.selectedCountries = this.data.selectedCountries;
    this.selectedStateProvinces = this.data.selectedStateProvinces;
    this.selectedCities = this.data.selectedCities;
    this.updateSelectedInformation();
    this.filteredLocations$ = this.locationControl.valueChanges.pipe(
      startWith(''),
      map((value) => this.filterLocationGroup(value)),
    );
  }

  private filterLocationGroup(value: string): any[] {
    if (!value) {
      value = '';
    }
    return this.data.availableLocations
      .map((group) => ({
        name: group.name,
        options: this.filterLocations(group.options, value),
      }))
      .filter((group) => group.options.length > 0);
  }

  private filterLocations(locations: Location[], value: string): any[] {
    const filterValue = value.toString().toLowerCase();
    return locations.filter((location) => {
      return (
        location.name.toLowerCase().indexOf(filterValue) > -1 &&
        this.selectedOptions.indexOf(location.id) === -1 &&
        !this.selectedOptions.some((r) => location.parent.includes(r))
      );
    });
  }

  addLocationOpt(event: MatAutocompleteSelectedEvent): void {
    // Find the selected option
    this.filteredLocations$
      .pipe(
        take(1),
        map((groups) => {
          for (const group of groups) {
            for (const location of group.options) {
              if (location.id === event.option.value.id) {
                if (location.parent.length === event.option.value.parent.length) {
                  let sameParents = true;
                  let i = 0;
                  while (i < location.parent.length) {
                    if (location.parent[i] !== event.option.value.parent[i]) {
                      sameParents = false;
                      break;
                    }
                    i = i + 1;
                  }
                  if (sameParents) {
                    return [group, location];
                  }
                }
              }
            }
          }
        }),
      )
      .subscribe(([group, location]) => {
        this.addLocation(group.name, location.id);
      });
  }

  addLocation(locationType: string, locationName?: string): void {
    const locationValue = locationName ? locationName : this.locationControl.value.trim();
    if (locationValue !== '') {
      this.locationControl.setValue('');

      if (locationType === COUNTRIES_LABEL) {
        const countryObject = this.data.availableLocations
          .filter((locationGroup) => locationGroup.name === COUNTRIES_LABEL)
          .map((locationGroup) =>
            locationGroup.options.filter((country) => country.id.toLowerCase() === locationValue.toLowerCase()),
          )[0][0];
        this.selectedCountries.push(countryObject);
      }
      if (locationType === STATEPROV_LABEL) {
        const stateprovObject = this.data.availableLocations
          .filter((locationGroup) => locationGroup.name === STATEPROV_LABEL)
          .map((locationGroup) =>
            locationGroup.options.filter((country) => country.id.toLowerCase() === locationValue.toLowerCase()),
          )[0][0];
        this.selectedStateProvinces.push(stateprovObject);
      }
      if (locationType === CITIES_LABEL) {
        const cityObject = this.data.availableLocations
          .filter((locationGroup) => locationGroup.name === CITIES_LABEL)
          .map((locationGroup) =>
            locationGroup.options.filter((country) => country.id.toLowerCase() === locationValue.toLowerCase()),
          )[0][0];
        this.selectedCities.push(cityObject);
      }
      this.updateSelectedInformation();
    }
  }

  removeLocation(locationType: number, locationName: string): void {
    if (locationType === 1) {
      this.selectedCountries = this.selectedCountries.filter((country) => country.id !== locationName);
    }
    if (locationType === 2) {
      this.selectedStateProvinces = this.selectedStateProvinces.filter((statProv) => statProv.id !== locationName);
    }
    if (locationType === 3) {
      this.selectedCities = this.selectedCities.filter((city) => city.id !== locationName);
    }
    this.updateSelectedInformation();
  }

  private updateSelectedInformation(): void {
    const selectedCountries = [...this.selectedCountries.map((country) => country.id)];
    this.selectedStateProvinces = this.selectedStateProvinces.filter(
      (location) => !selectedCountries.some((r) => location.parent.includes(r)),
    );
    const selectedStateProvinces = [...this.selectedStateProvinces.map((stateProv) => stateProv.id)];
    this.selectedCities = this.selectedCities.filter(
      (city) =>
        !selectedStateProvinces.some((r) => city.parent.includes(r)) &&
        !selectedCountries.some((r) => city.parent.includes(r)),
    );

    this.selectedOptions = [
      ...selectedCountries,
      ...selectedStateProvinces,
      ...this.selectedCities.map((city) => city.id),
    ];
    this.locationControl.setValue('');
  }

  close(newData: boolean): void {
    if (!newData) {
      this.dialogRef.close();
      return;
    }
    this.dialogRef.close({
      selectedCountries: this.selectedCountries,
      selectedStateProvinces: this.selectedStateProvinces,
      selectedCities: this.selectedCities,
    });
  }
}
